import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Chip, LoadingScreen } from '@cowoo/shared/ui-web';
import { ApiProblem, formatDate, formatTime, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { useAdminStore } from '../../lib/store';
import { EmptyState, ListCard, Page, Icon, type IconName } from '../../ui';

const TYPES = [
  'extraordinary_cancellation',
  'unmatched_312k',
  'dispute',
  'refund_request',
  'payout_credit',
  'other',
] as const;

type TaskType = (typeof TYPES)[number];

/** `Button` renders a `<button>`; a task's actions navigate, so they are links. */
const LINK_BUTTON =
  'border-line-strong text-ink font-body rounded-buttonSm inline-flex h-9 items-center justify-center border bg-white text-sm font-semibold hover:bg-fog';

/**
 * Where a task's object lives (§ 12's "Links / typical handling" column).
 *
 * The server says *what* a task points at (`refType`, `refId`); the route for
 * it is the backoffice's business, so the mapping is here rather than in the
 * payload.
 */
const LINK: Record<string, (id: string) => string> = {
  booking: (id) => `/buchungen/${id}`,
  invoice: (id) => `/rechnungen/${id}`,
  member: (id) => `/mitglieder/${id}`,
  user: (id) => `/mitglieder/${id}`,
};

/**
 * `/aufgaben` (10-web-admin § 12).
 *
 * Cards rather than a table: a task is a short piece of prose with two
 * buttons, and the useful part is the payload summary, which is a different
 * shape per type and does not fit columns.
 */
/** One picture per kind of task, as the board draws them. */
const TASK_ICON: Record<string, IconName> = {
  unmatched_312k: 'document',
  extraordinary_cancellation: 'document',
  dispute: 'warn',
  refund_request: 'refund',
  payout_credit: 'refund',
  other: 'task',
};

export function Tasks() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const me = useAdminStore((state) => state.profile?.userId ?? null);
  const [status, setStatus] = useState<'open' | 'done'>('open');
  const [type, setType] = useState<TaskType | 'all'>('all');
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  const tasks = useQuery({
    queryKey: qk.admin.area('staff-tasks', { status, type }),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/staff-tasks', {
        params: { query: { status, ...(type === 'all' ? {} : { type }) } },
      });
      if (error) throw error;
      return data;
    },
  });

  const patch = useMutation({
    mutationFn: async (input: {
      id: string;
      body: { status?: 'open' | 'done'; assignedTo?: string | null; note?: string };
    }) => {
      const { error } = await api.PATCH('/admin/staff-tasks/{id}', {
        params: { path: { id: input.id } },
        body: input.body,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('staff-tasks') });
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('nav') });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  if (tasks.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const items = tasks.data?.items ?? [];
  const counts = tasks.data?.counts;

  return (
    <Page>
      <ListCard
        title={t('admin.tasks.title')}
        actions={
          <select
            value={type}
            onChange={(event) => setType(event.target.value as TaskType | 'all')}
            className="border-line rounded-input text-adminBody h-9 border bg-white px-3 font-semibold"
          >
            <option value="all">{t('admin.tasks.allTypes')}</option>
            {TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`admin.tasks.type.${value}`)}
              </option>
            ))}
          </select>
        }
      >
        <div className="flex gap-2">
          {(['open', 'done'] as const).map((value) => (
            <Chip
              key={value}
              size="md"
              selected={value === status}
              onPress={() => setStatus(value)}
              label={`${t(`admin.tasks.${value}`)} · ${counts?.[value] ?? 0}`}
            />
          ))}
        </div>

        {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

        {items.length === 0 ? (
          <EmptyState>{t('admin.tasks.empty')}</EmptyState>
        ) : (
          <ul className="flex flex-col gap-3">
            {items.map((task) => {
              const href = task.refType && task.refId ? LINK[task.refType]?.(task.refId) : undefined;
              return (
                <li
                  key={task.id}
                  className="border-line rounded-input flex items-start gap-3.5 border px-4 py-3.5"
                >
                  {/* The board's `.ic`: a 40 px tile naming the kind of task
                      by its picture, red where money or a contract is at stake. */}
                  <span
                    className={`flex h-10 w-10 flex-none items-center justify-center rounded-xl ${
                      task.type === 'dispute' || task.type === 'extraordinary_cancellation'
                        ? 'bg-danger-bg text-danger-text'
                        : 'bg-teal-tint text-teal-dark'
                    }`}
                  >
                    <Icon name={TASK_ICON[task.type] ?? 'task'} size={20} />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex items-center gap-2.5">
                      <p className="text-adminBody text-ink font-bold">
                        {t(`admin.tasks.type.${task.type}`)}
                      </p>
                      <Chip
                        tone={task.status === 'open' ? 'warn' : 'success'}
                        label={t(`admin.tasks.${task.status}`)}
                      />
                    </div>
                    <p className="text-adminSmall text-muted">
                      {t('admin.tasks.received', {
                        date: formatDate(task.createdAt),
                        time: formatTime(task.createdAt),
                      })}
                      {task.assignedToName ? ` · ${task.assignedToName}` : ''}
                    </p>
                    {summarise(task.payload) ? (
                      <p className="text-adminSmall text-text">{summarise(task.payload)}</p>
                    ) : null}
                    {task.doneNote ? (
                      <p className="text-adminSmall text-muted">
                        {t('admin.tasks.doneNote', { note: task.doneNote })}
                      </p>
                    ) : null}
                  </div>

                  {task.status === 'open' ? (
                    <div className="flex w-[170px] flex-none flex-col gap-2">
                      {href ? (
                        <Link to={href} className={LINK_BUTTON}>
                          {t(`admin.tasks.openObject.${task.refType}`)}
                        </Link>
                      ) : null}
                      {/* § 12 — a § 312k cancellation with no matching account
                          is handled by recording it by hand, with the original
                          receipt time. The task points at no object, so the
                          action comes from its type. */}
                      {task.type === 'unmatched_312k' ? (
                        <Link to="/mitglieder" className={LINK_BUTTON}>
                          {t('admin.members.recordCancellation')}
                        </Link>
                      ) : null}
                      {!task.assignedTo && me ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => patch.mutate({ id: task.id, body: { assignedTo: me } })}
                        >
                          {t('admin.tasks.assignToMe')}
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        onClick={() => {
                          const note = window.prompt(t('admin.tasks.notePrompt')) ?? '';
                          patch.mutate({
                            id: task.id,
                            body: { status: 'done', ...(note.trim() ? { note: note.trim() } : {}) },
                          });
                        }}
                      >
                        {t('admin.tasks.markDone')}
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </ListCard>
    </Page>
  );
}

/**
 * The payload of `05-api` § 11 is a free-shaped object per task type, so the
 * summary is whatever scalar fields it carries, in order. Rendering it beats
 * hiding it: the payload is the only thing that says *which* booking or which
 * cancellation a task is about.
 */
function summarise(payload: Record<string, unknown>): string {
  return Object.entries(payload)
    .filter(([, value]) => typeof value === 'string' || typeof value === 'number')
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(' · ');
}
